import { Group, GroupFilterCondition } from "@fairfleet/geotab";

type ConditionNode = { groupId?: string; isNegated?: boolean; groupFilterConditions?: ConditionNode[] };
type Attributes = { [key: string]: string | number | boolean | Date | string[] };
type UserScope = {
  [key: string]: unknown;
  companyGroups?: { id: string }[];
  userGroupFilterCondition?: GroupFilterCondition;
};

/**
 * User scope for a `Get User` search, mirroring {@link createGroupFilter} for devices:
 * the groups picked on the page win, because MyGeotab's advanced condition is not updated
 * when the page's own group filter changes. Without picked groups the advanced condition
 * applies; `userGroupFilterCondition` only works with several items, so a single item
 * (or a leaf) is sent as `companyGroups` instead, which keeps the search scoped.
 */
export default function createUserGroupFilter(
  companyGroups?: Group[],
  userGroupFilterCondition?: GroupFilterCondition | null,
  additionalAttributes?: Attributes,
): UserScope {
  if (companyGroups?.length) return { ...additionalAttributes, companyGroups };
  if (!userGroupFilterCondition) return { ...additionalAttributes };

  let node = userGroupFilterCondition as ConditionNode;
  let negated = !!node.isNegated;
  while (node.groupFilterConditions?.length === 1) {
    node = node.groupFilterConditions[0];
    if (node.isNegated) negated = !negated;
  }

  if (node.groupFilterConditions?.length) {
    // Unwrapping dropped the wrappers' negations; carry the net negation on the kept condition.
    const kept = negated === !!node.isNegated ? node : { ...node, isNegated: negated };
    return { ...additionalAttributes, userGroupFilterCondition: kept as GroupFilterCondition };
  }
  // `companyGroups` cannot express "NOT group X"; scoping it positively would invert the filter,
  // so a negated leaf falls back to no advanced scope (same as any unsupported condition).
  if (node.groupId && !negated) return { ...additionalAttributes, companyGroups: [{ id: node.groupId }] };
  return { ...additionalAttributes };
}
